import { normalizeTimeValue, parseTimeToMinutesValue } from '@/helpers/timeHelper';
import { DateHelper } from '@/helpers/dateHelper';

type StartDateTimeResult = {
  raw: string | null;
  local: string | null;
  iso: string | null;
  display: string | null;
  time: string | null;
};

export { normalizeTimeValue, parseTimeToMinutesValue };

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function parseDateInput(dateValue: unknown): Date | null {
  if (!dateValue) {
    return null;
  }
  if (dateValue instanceof Date) {
    const direct = new Date(dateValue.getTime());
    return Number.isNaN(direct.getTime()) ? null : direct;
  }
  if (typeof dateValue === 'string' && DATE_ONLY_PATTERN.test(dateValue)) {
    const local = new Date(`${dateValue}T00:00:00`);
    return Number.isNaN(local.getTime()) ? null : local;
  }
  const parsed = new Date(String(dateValue));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function toLocalDateValue(dateValue: unknown): string | null {
  if (!dateValue) {
    return null;
  }
  const d = parseDateInput(dateValue);
  if (!d) {
    return null;
  }
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function toLocalTimeValue(dateValue: unknown): string | null {
  const d = parseDateInput(dateValue);
  if (!d) {
    return null;
  }
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}

export function buildStartDateTimeValue(dateValue: unknown, timeValue: unknown): StartDateTimeResult {
  const rawDate = Array.isArray(dateValue) ? (dateValue.length ? dateValue[0] : null) : dateValue;
  const rawTime = Array.isArray(timeValue) ? (timeValue.length ? timeValue[0] : null) : timeValue;

  if (!rawDate) {
    return {
      raw: null,
      local: null,
      iso: null,
      display: null,
      time: typeof rawTime === 'string' ? rawTime : rawTime ? String(rawTime) : null,
    };
  }

  const normalizedTime = normalizeTimeValue(rawTime);
  const rawDateStr = String(rawDate);
  let composed = rawDateStr;
  let local = null;
  if (normalizedTime) {
    const suffix = rawDateStr.includes('T') ? '' : 'T';
    local = `${rawDateStr}${suffix}${normalizedTime}:00`;
    composed = local;
  }

  const parsed = new Date(composed);
  if (Number.isNaN(parsed.getTime())) {
    return {
      raw: composed,
      local,
      iso: null,
      display: rawTime ? `${rawDateStr} ${rawTime}` : rawDateStr,
      time: normalizedTime ? normalizedTime : (rawTime ? String(rawTime) : null),
    };
  }

  const locale = typeof navigator !== 'undefined' && navigator.language ? navigator.language : 'es-ES';
  const display = parsed.toLocaleString(locale, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).replace('.', '');

  return {
    raw: composed,
    local: local ? local : composed,
    iso: DateHelper.toSystemString(parsed),
    display,
    time: normalizedTime ? normalizedTime : (rawTime ? String(rawTime) : null),
  };
}
